import React, { useEffect, useState } from 'react';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import { getPropertyById } from '../../../api/services/propertyService';
import { getUnitRequestIdFromNotification } from '../../shared/Notifications/notificationUtils';
import { filterPropertiesByScope } from '../../../utils/propertyScope';
import PropertyUnitRequestsView from './PropertyUnitRequests/PropertyUnitRequestsView';

const STORAGE_KEY = 'agent_unit_requests_property';

const AgentUnitRequests = ({ onNavigate, navigationState = {} }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultPropertyId, setDefaultPropertyId] = useState(null);
  const [requestNav, setRequestNav] = useState(null);

  useEffect(() => {
    const hasIntent =
      navigationState?.requestId ||
      navigationState?.requestResolveHint ||
      navigationState?.focusRequestOnly;

    if (!hasIntent) return;

    const idFromNotification = navigationState.notification
      ? getUnitRequestIdFromNotification(navigationState.notification)
      : null;
    const requestId = navigationState.requestId || idFromNotification || null;

    setRequestNav({
      requestId,
      focusRequestOnly: Boolean(navigationState.focusRequestOnly),
      openPaymentEvidence: Boolean(navigationState.openPaymentEvidence),
      requestResolveHint: requestId ? null : navigationState.requestResolveHint || null,
    });
  }, [
    navigationState?.requestId,
    navigationState?.notification,
    navigationState?.requestResolveHint,
    navigationState?.focusRequestOnly,
    navigationState?.openPaymentEvidence,
  ]);

  useEffect(() => {
    try {
      const id = sessionStorage.getItem(STORAGE_KEY);
      if (id) {
        setDefaultPropertyId(id);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyAgentAssignments({ page: 0, size: 100 });
        const content = res?.data?.content || [];
        const list = Array.isArray(content) ? content : [];
        const byId = new Map();
        list.forEach((a) => {
          const id = a.propertyId;
          if (!id || byId.has(id)) return;
          byId.set(id, { id, name: a.propertyName || 'Property' });
        });
        const agentProperties = [...byId.values()];

        const enrichedProperties = await Promise.all(
          agentProperties.map(async (property) => {
            if (!property?.id) return property;
            try {
              const detailRes = await getPropertyById(property.id, true);
              if (detailRes?.status === 'success' && detailRes?.data) {
                return { ...property, ...detailRes.data };
              }
            } catch (e) {
              // ignore
            }
            return property;
          })
        );
        
        const scopedProperties = filterPropertiesByScope(enrichedProperties, 'landlord', {
          includeUnscoped: true,
          inferFromUnits: true,
        });

        if (!cancelled) setProperties(scopedProperties);
      } catch (e) {
        console.error(e);
        if (!cancelled) setProperties([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearRequestNav = () => setRequestNav(null);

  return (
    <PropertyUnitRequestsView
      properties={properties}
      loadingProperties={loading}
      pageTitle="Unit requests"
      emptyPropertiesMessage="You have no assigned properties yet. Accept an invitation from a landlord to see tenant unit requests here."
      defaultPropertyId={defaultPropertyId}
      variant="agent"
      onNavigate={onNavigate}
      highlightRequestId={requestNav?.requestId}
      focusRequestOnly={requestNav?.focusRequestOnly}
      openPaymentEvidence={requestNav?.openPaymentEvidence}
      requestResolveHint={requestNav?.requestResolveHint}
      onFocusExit={clearRequestNav}
    />
  );
};

export default AgentUnitRequests;
